import { Button, Form, Row, Col, Descriptions, Table, Input, Flex, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { useEffect } from 'react';
import { User, Scheduler } from '../../api/go';
import { DateTime } from 'luxon';
import { OSInfoLog } from '../../api/type';

type QueryForm = {
  hardware?: string;
};

interface OSLogEx extends OSInfoLog {
  nick?: string;
  status?: number;
}

const columns: ColumnsType<OSLogEx> = [
  {
    width: 220,
    title: '日期',
    dataIndex: 'date',
    key: 'date',
    render(value) {
      return DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm:ss');
    },
  },
  {
    width: 280,
    title: 'UserId',
    dataIndex: 'user_id',
    key: 'user_id',
    render: (_, record) => {
      return (
        <div>
          <div>{record.user_id}</div>
          <div>{record.nick}</div>
        </div>
      );
    },
  },
  {
    width: 120,
    title: '账号状态',
    dataIndex: 'status',
    key: 'status',
    render: (value) => {
      let text = '未知';

      switch (value) {
        case 0:
          text = '封禁';
          break;
        case 1:
          text = '正常';
          break;
        case 2:
          text = '未知';
          break;
      }

      return <div style={value === 0 ? { color: 'red', fontWeight: 'bold' } : {}}>{text}</div>;
    },
  },
  {
    width: 280,
    title: 'IP 信息',
    dataIndex: 'ipInfo',
    key: 'ipInfo',
    render(_, record) {
      // Assuming IP info is part of the data, adjust as necessary
      if (!record.data.ip) {
        return <div>未知</div>;
      }
      return (
        <div>
          <div>{record.data.ip}</div>
          <div>
            {record.data.ip_location.region} {record.data.ip_location.city} - {record.data.ip_location.isp}
          </div>
        </div>
      );
    },
  },
  {
    width: 280,
    title: '系统配置',
    dataIndex: 'systemConfig',
    key: 'systemConfig',
    render(_, record) {
      return (
        <div>
          <div>{record.data.platform}</div>
          <div>{record.data.platform_version}</div>
          <div>
            {record.data.cpu} / 内存{(record.data.memory / 1024).toFixed(1)}G
          </div>
          {record.data.gpu_list.map((gpu, index) => (
            <div key={index}>
              GPU: {gpu.name} (驱动版本: {gpu.driver_version})
            </div>
          ))}
        </div>
      );
    },
  },
  {
    width: 280,
    title: '机器标识',
    dataIndex: 'uuid',
    key: 'uuid',
    render(_, record) {
      return (
        <div>
          <div>系统标识 : {record.data.uuid_os}</div>
          <div>硬件标识 : {record.data.uuid_hardware}</div>
          {record.data.gpu_list.map((gpu, index) => (
            <div key={index}>GPU设备ID : {gpu.pnp_device_id}</div>
          ))}
        </div>
      );
    },
  },
];

const DefaultData = {
  loading: false,
  logList: [] as OSLogEx[],
};

const store = atom(DefaultData);

async function loadLog(query: QueryForm) {
  store.set({ ...store.get(), loading: true });

  const logList = await User.OSInfoLogByUUID(query.hardware!);

  const userIdSet = new Set<string>();
  for (const log of logList) {
    userIdSet.add(log.user_id);
  }

  // 获取用户信息
  const userIdList = Array.from(userIdSet);
  const userInfoList = await User.infoList(userIdList);
  const userInfoMap: Record<string, (typeof userInfoList)[0]> = {};
  for (const userInfo of userInfoList) {
    userInfoMap[userInfo.UserId] = userInfo;
  }

  // 将用户信息合并到日志中
  const logListEx: OSLogEx[] = logList.map((log) => ({
    ...log,
    nick: userInfoMap[log.user_id]?.NickName,
    status: userInfoMap[log.user_id]?.Status,
  }));

  store.set({
    loading: false,
    logList: logListEx,
  });
}

function clearState() {
  store.set(DefaultData);
}

// 根据 UUID 查询用户列表
export default function UserListByUUID() {
  useEffect(() => {
    return () => {
      clearState();
    };
  }, []);

  const state = useStore(store);

  return (
    <div>
      <Row>
        <Col span={8}>
          <Form<QueryForm>
            name="list"
            layout="horizontal"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            style={{ maxWidth: 600 }}
            onFinish={(values: QueryForm) => {
              if (!values.hardware) {
                message.error('请选择硬件UUID');
                return;
              }
              loadLog(values);
            }}
            autoComplete="off"
          >
            <Form.Item<QueryForm> label="硬件UUID" name="hardware">
              <Input disabled={state.loading} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label={null}>
              <Button type="primary" htmlType="submit" loading={state.loading}>
                查询
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <div>
        <Table dataSource={state.logList} columns={columns} pagination={false} />
      </div>
    </div>
  );
}
